import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined'
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined'
import { Alert, Grid } from '@mui/material'

import {
  COLOR_JOBS_CX,
  COLOR_JOBS_OP,
  COLOR_SUPPLIERS_ACTIVATED,
  COLOR_SUPPLIERS_SIGNED_UP,
} from 'components/reports/Metrics/constants/NetworkGrowth'
import {
  useGetNetworkGrowthCxJobsPerSupplierQuery,
  useGetNetworkGrowthJobsCompletedInCxQuery,
  useGetNetworkGrowthJobsCompletedInOpQuery,
  useGetNetworkGrowthOpJobsPerSupplierQuery,
  useGetNetworkGrowthSuppliersSignedUpToOpQuery,
  useGetNetworkGrowthSuppliersWithCompletedOpJobQuery,
} from 'generated/graphql'

import { CumulativeMetricCard, JobsPerSupplierCard } from '../components'
import { useNetworkGrowthQueryInput } from './useNetworkGrowthQueryInput'

export const NetworkGrowthCards = () => {
  const { hasValidRange, input } = useNetworkGrowthQueryInput()

  const queryOptions = {
    variables: { input },
    skip: !hasValidRange,
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-and-network' as const,
  }

  const signedUp = useGetNetworkGrowthSuppliersSignedUpToOpQuery(queryOptions)
  const activated = useGetNetworkGrowthSuppliersWithCompletedOpJobQuery(queryOptions)
  const jobsOp = useGetNetworkGrowthJobsCompletedInOpQuery(queryOptions)
  const jobsCx = useGetNetworkGrowthJobsCompletedInCxQuery(queryOptions)
  const opPerSupplier = useGetNetworkGrowthOpJobsPerSupplierQuery(queryOptions)
  const cxPerSupplier = useGetNetworkGrowthCxJobsPerSupplierQuery(queryOptions)

  const signedUpSeries =
    signedUp.data?.networkGrowthSuppliersSignedUpToOp ??
    signedUp.previousData?.networkGrowthSuppliersSignedUpToOp
  const activatedSeries =
    activated.data?.networkGrowthSuppliersWithCompletedOpJob ??
    activated.previousData?.networkGrowthSuppliersWithCompletedOpJob
  const jobsOpSeries =
    jobsOp.data?.networkGrowthJobsCompletedInOp ??
    jobsOp.previousData?.networkGrowthJobsCompletedInOp
  const jobsCxSeries =
    jobsCx.data?.networkGrowthJobsCompletedInCx ??
    jobsCx.previousData?.networkGrowthJobsCompletedInCx
  const opHistogram =
    opPerSupplier.data?.networkGrowthOpJobsPerSupplier ??
    opPerSupplier.previousData?.networkGrowthOpJobsPerSupplier
  const cxHistogram =
    cxPerSupplier.data?.networkGrowthCxJobsPerSupplier ??
    cxPerSupplier.previousData?.networkGrowthCxJobsPerSupplier

  const supplierAxisMax = Math.max(
    signedUpSeries?.summaryCount ?? 0,
    activatedSeries?.summaryCount ?? 0,
  )
  const jobsAxisMax = Math.max(jobsOpSeries?.summaryCount ?? 0, jobsCxSeries?.summaryCount ?? 0)

  const hasError =
    signedUp.error ||
    activated.error ||
    jobsOp.error ||
    jobsCx.error ||
    opPerSupplier.error ||
    cxPerSupplier.error

  return (
    <>
      {hasError && (
        <Alert severity='error' sx={{ mb: 2 }}>
          Some network growth metrics failed to load. Please try again.
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <CumulativeMetricCard
            title='Suppliers signed up to OP'
            summaryCaption='Total signed up'
            yAxisLabel='Cumulative suppliers'
            color={COLOR_SUPPLIERS_SIGNED_UP}
            icon={<GroupAddOutlinedIcon fontSize='small' />}
            info='Suppliers holding an Owner or Transport Controller account.'
            legendLabel='Cumulative unique suppliers'
            series={signedUpSeries}
            suggestedMax={supplierAxisMax}
            loading={signedUp.loading && !signedUpSeries}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CumulativeMetricCard
            title='Suppliers who completed at least one job in OP'
            summaryCaption='Total suppliers'
            yAxisLabel='Cumulative suppliers'
            color={COLOR_SUPPLIERS_ACTIVATED}
            icon={<TaskAltOutlinedIcon fontSize='small' />}
            info='Suppliers that completed at least one OP job in the selected period.'
            legendLabel='Cumulative unique suppliers'
            series={activatedSeries}
            suggestedMax={supplierAxisMax}
            loading={activated.loading && !activatedSeries}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CumulativeMetricCard
            title='Jobs completed in OP'
            summaryCaption='Total jobs'
            yAxisLabel='Cumulative jobs'
            color={COLOR_JOBS_OP}
            icon={<LocalShippingOutlinedIcon fontSize='small' />}
            info='Jobs whose allocated supplier is in the Onepoint network, dated on the final delivery signature.'
            series={jobsOpSeries}
            suggestedMax={jobsAxisMax}
            loading={jobsOp.loading && !jobsOpSeries}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <CumulativeMetricCard
            title='Jobs completed in CX'
            summaryCaption='Total jobs'
            yAxisLabel='Cumulative jobs'
            color={COLOR_JOBS_CX}
            icon={<SwapHorizOutlinedIcon fontSize='small' />}
            info='Jobs completed through Courier Exchange, dated on the final delivery signature.'
            series={jobsCxSeries}
            suggestedMax={jobsAxisMax}
            loading={jobsCx.loading && !jobsCxSeries}
          />
        </Grid>

        <Grid item xs={12}>
          <JobsPerSupplierCard
            title='OP: Number of jobs completed per supplier'
            subtitle='Distribution of OP suppliers by total jobs completed in OP (within selected period).'
            totalCaption='Total OP suppliers'
            color={COLOR_JOBS_OP}
            info='How OP work is spread across suppliers. Each supplier is placed in one bucket by its total completed jobs in the period.'
            histogram={opHistogram}
            loading={opPerSupplier.loading && !opHistogram}
          />
        </Grid>

        <Grid item xs={12}>
          <JobsPerSupplierCard
            title='CX: Number of jobs completed per supplier'
            subtitle='Distribution of CX suppliers by total jobs completed in CX (within selected period).'
            totalCaption='Total CX suppliers'
            color={COLOR_JOBS_CX}
            info='How CX work is spread across suppliers. Each supplier is placed in one bucket by its total completed jobs in the period.'
            histogram={cxHistogram}
            loading={cxPerSupplier.loading && !cxHistogram}
          />
        </Grid>
      </Grid>
    </>
  )
}
